import { useMemo } from "react";

type SearchableField = string | number | undefined;
type SearchTerm = {
  text: string;
  type: "include" | "exclude" | "exact";
};

interface SearchConfig<T> {
  searchTerm: string;
  data: T[];
  fields: (item: T) => SearchableField[];
}

const checkFieldMatch = (field: SearchableField, term: string): boolean => {
  if (field === undefined) return false;
  return String(field).toLowerCase().includes(term.toLowerCase());
};

const parseSearchTerms = (searchTerm: string): SearchTerm[] => {
  const terms: SearchTerm[] = [];
  let currentTerm = "";
  let inQuotes = false;

  const addTerm = (term: string, type: "include" | "exclude" | "exact") => {
    if (term.trim()) {
      terms.push({ text: term.trim(), type });
    }
  };

  for (let i = 0; i < searchTerm.length; i++) {
    const char = searchTerm[i];

    if (char === '"') {
      if (inQuotes) {
        addTerm(currentTerm, "exact");
        currentTerm = "";
      }
      inQuotes = !inQuotes;
    } else if (!inQuotes && (char === "+" || char === "|")) {
      addTerm(currentTerm, "include");
      currentTerm = "";
    } else if (
      !inQuotes &&
      char === "-" &&
      (i === 0 || searchTerm[i - 1] === " ")
    ) {
      if (currentTerm) addTerm(currentTerm, "include");
      currentTerm = "";
    } else if (!inQuotes && char === " " && currentTerm) {
      addTerm(currentTerm, currentTerm.startsWith("-") ? "exclude" : "include");
      currentTerm = "";
    } else {
      currentTerm += char;
    }
  }

  if (currentTerm) {
    addTerm(currentTerm, currentTerm.startsWith("-") ? "exclude" : "include");
  }

  return terms;
};

const checkItemMatch = <T>(
  item: T,
  searchTerm: string,
  getFields: (item: T) => SearchableField[]
): boolean => {
  return getFields(item).some((field) => checkFieldMatch(field, searchTerm));
};

export const useAdvancedSearch = <T>({
  searchTerm,
  data,
  fields,
}: SearchConfig<T>): T[] => {
  return useMemo(() => {
    if (!searchTerm) return data;

    const searchTerms = parseSearchTerms(searchTerm);

    return data.filter((item) => {
      // Gérer les termes exclus d'abord
      const excludeTerms = searchTerms.filter(
        (term) => term.type === "exclude"
      );
      for (const term of excludeTerms) {
        if (checkItemMatch(item, term.text.replace(/^-/, ""), fields)) {
          return false;
        }
      }

      // Gérer les termes exacts
      const exactTerms = searchTerms.filter((term) => term.type === "exact");
      for (const term of exactTerms) {
        if (!checkItemMatch(item, term.text, fields)) {
          return false;
        }
      }

      // Gérer les termes inclus
      const includeTerms = searchTerms.filter(
        (term) => term.type === "include"
      );
      if (includeTerms.length === 0) return true;

      // Si les termes sont séparés par des +, tous doivent correspondre
      if (searchTerm.includes("+")) {
        return includeTerms.every((term) =>
          checkItemMatch(item, term.text, fields)
        );
      }

      // Si les termes sont séparés par des |, au moins un doit correspondre
      if (searchTerm.includes("|")) {
        return includeTerms.some((term) =>
          checkItemMatch(item, term.text, fields)
        );
      }

      // Par défaut, tous les termes doivent correspondre
      return includeTerms.every((term) =>
        checkItemMatch(item, term.text, fields)
      );
    });
  }, [searchTerm, data, fields]);
};
